/**
 * IconSelect — a form field for choosing an icon from a curated grid.
 *
 * Renders as a trigger button (showing the selected icon or placeholder)
 * that opens a popover with a grid of icons. Follows the same field
 * pattern as Select (label + trigger + popover).
 */

import { ChevronDown } from '@untitledui/icons';
import { useCallback, useRef, useState } from 'react';

import { cx } from '@/utils/cx';

import { getIconComponentByName } from './EntityTypeIcon';
import { CURATED_ICONS } from './IconPicker';

export interface IconSelectProps {
  /** Label shown above the field */
  label?: string;
  /** Currently selected icon name */
  value: string | null;
  /** Callback when an icon is selected */
  onChange: (iconName: string) => void;
  /** Placeholder text when no icon is selected */
  placeholder?: string;
  /** Whether the field is disabled */
  isDisabled?: boolean;
  /** Accessible label for the trigger */
  'aria-label'?: string;
}

export function IconSelect({
  label,
  value,
  onChange,
  placeholder = 'Select an icon',
  isDisabled = false,
  'aria-label': ariaLabel,
}: IconSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const SelectedIcon = value ? getIconComponentByName(value) : null;

  const handleSelect = useCallback(
    (iconName: string) => {
      onChange(iconName);
      setIsOpen(false);
      // Return focus to trigger after selection
      triggerRef.current?.focus();
    },
    [onChange]
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      triggerRef.current?.focus();
    }
  }, []);

  return (
    <div className='relative'>
      {label && (
        <label className='block text-sm font-medium text-text-primary mb-2'>
          {label}
        </label>
      )}

      {/* Trigger */}
      <button
        ref={triggerRef}
        type='button'
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={isDisabled}
        aria-label={ariaLabel ?? label ?? 'Select icon'}
        aria-haspopup='dialog'
        aria-expanded={isOpen}
        className={cx(
          'relative flex w-full items-center rounded-lg bg-primary shadow-xs ring-1 ring-primary ring-inset',
          'py-2 px-3 text-sm transition duration-100 ease-linear',
          isOpen && 'ring-2 ring-brand',
          isDisabled
            ? 'cursor-not-allowed bg-disabled_subtle text-disabled'
            : 'cursor-pointer hover:bg-secondary'
        )}
      >
        {SelectedIcon ? (
          <span className='flex items-center gap-2 flex-1 min-w-0'>
            <SelectedIcon className='w-5 h-5 text-tertiary flex-shrink-0' />
            <span className='truncate text-text-primary'>{value}</span>
          </span>
        ) : (
          <span className='flex-1 text-left text-text-tertiary'>
            {placeholder}
          </span>
        )}
        <ChevronDown
          className={cx(
            'w-4 h-4 text-tertiary flex-shrink-0 ml-2 transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Popover */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className='fixed inset-0 z-40'
            onClick={() => setIsOpen(false)}
          />

          {/* Grid popover */}
          <div
            ref={popoverRef}
            role='dialog'
            aria-label='Choose an icon'
            onKeyDown={handleKeyDown}
            className='absolute z-50 mt-1 w-full bg-bg-primary border border-border-secondary rounded-lg shadow-lg p-2 max-h-64 overflow-y-auto'
          >
            <div
              role='radiogroup'
              aria-label='Icon options'
              className='grid grid-cols-6 gap-1'
            >
              {CURATED_ICONS.map(iconName => {
                const IconComp = getIconComponentByName(iconName);
                const isSelected = value === iconName;

                return (
                  <button
                    key={iconName}
                    type='button'
                    role='radio'
                    aria-checked={isSelected}
                    aria-label={iconName}
                    onClick={() => handleSelect(iconName)}
                    className={cx(
                      'flex items-center justify-center p-2 rounded-md',
                      'transition-colors cursor-pointer',
                      isSelected
                        ? 'bg-brand-50 ring-2 ring-brand-600'
                        : 'hover:bg-bg-secondary',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500'
                    )}
                  >
                    <IconComp
                      className={cx(
                        'w-5 h-5',
                        isSelected ? 'text-fg-brand-primary' : 'text-tertiary'
                      )}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
